import { useCallback } from 'react';
import { isHapticsEnabled } from '@/utils/haptics';

interface HapticOptions {
  duration?: number;
  intensity?: 'light' | 'medium' | 'heavy';
}

export const useHapticFeedback = () => {
  const isSupported = useCallback(() => {
    if (typeof navigator === 'undefined') return false;
    return 'vibrate' in navigator || 'mozVibrate' in navigator;
  }, []);

  const isEnabled = useCallback(() => {
    return isHapticsEnabled();
  }, []);

  const hasUserActivation = useCallback(() => {
    if (typeof navigator === 'undefined') return false;
    const nav = navigator as Navigator & {
      userActivation?: {
        hasBeenActive?: boolean;
      };
    };

    if (!nav.userActivation) return true;
    return nav.userActivation.hasBeenActive !== false;
  }, []);

  const vibrate = useCallback((pattern: number | number[]) => {
    if (!isSupported() || !isEnabled() || !hasUserActivation()) return false;

    try {
      // Handle vendor prefixes
      const nav = navigator as any;
      if (nav.vibrate) {
        return nav.vibrate(pattern);
      } else if (nav.mozVibrate) {
        return nav.mozVibrate(pattern);
      }
    } catch (error) {
      console.error('Haptic feedback error:', error);
    }
    return false;
  }, [hasUserActivation, isEnabled, isSupported]);

  const impact = useCallback((options: HapticOptions = {}) => {
    const { duration = 10, intensity = 'medium' } = options;
    
    const intensityMap = {
      light: duration * 0.5,
      medium: duration,
      heavy: duration * 1.5,
    };

    return vibrate(intensityMap[intensity]);
  }, [vibrate]);

  const selection = useCallback(() => {
    return vibrate(10);
  }, [vibrate]);

  const notification = useCallback((type: 'success' | 'warning' | 'error' = 'success') => {
    const patterns = {
      success: [10, 20, 10],
      warning: [20, 10, 20],
      error: [50, 10, 50],
    };

    return vibrate(patterns[type]);
  }, [vibrate]);

  const pattern = useCallback((customPattern: number[]) => {
    return vibrate(customPattern);
  }, [vibrate]);

  return {
    isSupported,
    isEnabled,
    impact,
    selection,
    notification,
    pattern,
  };
};

// Hook specifically for game events
export const useGameHaptics = () => {
  const haptics = useHapticFeedback();

  const onMove = useCallback(() => {
    haptics.selection();
  }, [haptics]);

  const onCollision = useCallback(() => {
    haptics.impact({ duration: 30, intensity: 'heavy' });
  }, [haptics]);

  const onPowerupCollect = useCallback(() => {
    haptics.notification('success');
  }, [haptics]);

  const onLevelComplete = useCallback(() => {
    haptics.pattern([20, 50, 20, 50, 20]);
  }, [haptics]);

  const onGameOver = useCallback(() => {
    haptics.impact({ duration: 100, intensity: 'heavy' });
  }, [haptics]);

  return {
    onMove,
    onCollision,
    onPowerupCollect,
    onLevelComplete,
    onGameOver,
    ...haptics,
  };
};
